import { environment } from '../../environments/environment';

/**
 * Per-deployment settings, loaded at RUNTIME rather than baked into the build.
 *
 * `apihost` lived in `src/environments/environment.prod.ts`, where it read
 * `http://fusion-stg-server.revenuedev.org` -- one country's staging server,
 * over plain http, compiled into every production artifact. That is wrong three
 * ways at once:
 *
 *  - **It ties the artifact to a country.** The same tested bundle should be
 *    publishable to any deployment. Baking the host in means a build per
 *    country, and a build is where "one value differs" becomes "these are
 *    different releases". This is the same correction already made in
 *    fusion-web-portal for `country`.
 *  - **It cannot be corrected without a rebuild.** A wrong API host is not a
 *    cosmetic defect: the application does not work at all.
 *  - **Plain http breaks under TLS.** Served from an https origin, every call
 *    to an `http://` host is blocked as mixed content before it leaves the
 *    browser. Nothing loads, and the console error names the wrong culprit.
 *
 * So it comes from `/app-config.json`, fetched before the app bootstraps.
 *
 * ## Why the default is the empty string
 *
 * Empty means *same origin* -- `apiUrl('api/user')` yields `/api/user`, a
 * relative URL. That is the intended production shape: the CDN serves the SPA
 * at `/` and forwards the API path prefixes to the server, so the browser
 * makes first-party requests. No CORS allowlist, no `SameSite=None`, and no
 * host to get wrong. A deployment that genuinely splits the API onto its own
 * hostname sets `apiHost` and accepts what follows: a CORS allowlist on the
 * server, `SameSite=None` on the session cookie, AND a real
 * Content-Security-Policy response header at the edge naming that origin in
 * `connect-src` -- the policy in `src/index.prod.html` is `'self'` only, and a
 * meta tag cannot be adjusted per deployment.
 *
 * ## Why fetch-before-bootstrap and not an APP_INITIALIZER
 *
 * `SharedService.apiUrl` and `AuthInterceptor.isTrustedApiUrl` read this
 * synchronously, on paths that cannot become asynchronous without a wide
 * refactor. The cost is one request for a file measured in bytes.
 *
 * ## Failure is not fatal
 *
 * The file is fetched at runtime, so it can be missing, truncated or malformed
 * in a way no build step would catch. Every path here falls back to the
 * compiled default rather than throwing, because same-origin is a good guess
 * and an application that fails to start is not.
 */

/** Shape of `/app-config.json`. Every field is optional in the file. */
export interface AppConfig {
  /**
   * Origin (optionally with a path prefix) the API is reached at, with no
   * trailing slash. Empty -- the default -- means same origin.
   */
  readonly apiHost: string;

  /**
   * The languages this deployment offers, as the codes naming files in
   * `src/assets/i18n/`. Order is the order of the switcher.
   *
   * Per-deployment for the same reason as `apiHost`: Suriname needs Dutch and
   * has no use for French, while the francophone deployments need French and
   * have no use for Dutch. Shipping the union and hiding half of it per country
   * would mean a build per country, which is what this file exists to avoid.
   */
  readonly languages: readonly string[];
}

export const APP_CONFIG_URL = '/app-config.json';

/**
 * The compiled fallback. `environment.apihost` remains the DEV value so
 * `ng serve` still talks to a local server without a config file; the
 * production environment sets it empty.
 */
/**
 * The languages a bundle can actually serve -- one entry per file in
 * `src/assets/i18n/`. A code outside this set names a file that does not exist,
 * and ngx-translate answers a missing bundle by rendering raw keys, so the
 * screen fills with `HEADER.LOGOUT` instead of words.
 */
export const SUPPORTED_LANGUAGES = ['en', 'fr', 'nl'] as const;

/**
 * The compiled fallback. `environment.apihost` remains the DEV value so
 * `ng serve` still talks to a local server without a config file; the
 * production environment sets it empty.
 *
 * `languages` defaults to en + fr, which is what every existing deployment
 * already offers -- so a deployment that never sets it is unchanged.
 */
const DEFAULTS: AppConfig = { apiHost: environment.apihost || '', languages: ['en', 'fr'] };

let current: AppConfig = DEFAULTS;

/** The active configuration. Defaults until `loadAppConfig` has resolved. */
export function appConfig(): AppConfig {
  return current;
}

/**
 * The API host, for the two call sites that build URLs from it.
 *
 * Prefer this over reading `environment.apihost`: that value is now only the
 * build-time fallback, and using it directly reintroduces the bug this module
 * exists to fix.
 */
export function apiHost(): string {
  return current.apiHost;
}

/** The languages to offer, in switcher order. Never empty. */
export function languages(): readonly string[] {
  return current.languages;
}

/**
 * Coerce the configured language list into something safe to render.
 *
 * Exported for testing. Strict in one direction only: an unrecognised code is
 * dropped rather than refused outright, because a deployment listing four
 * languages of which one is a typo should still offer the other three -- but a
 * list that ends up empty falls back, since a switcher with nothing in it hides
 * the only way to change language.
 */
export function normalizeLanguages(raw: unknown): readonly string[] {
  if (!Array.isArray(raw)) return DEFAULTS.languages;

  const seen = new Set<string>();
  const kept: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const code = entry.trim().toLowerCase();
    if (code === '' || seen.has(code)) continue;
    if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(code)) {
      warn(`language "${code}" has no bundle in assets/i18n; ignoring it.`);
      continue;
    }
    seen.add(code);
    kept.push(code);
  }
  return kept.length ? kept : DEFAULTS.languages;
}

/**
 * Coerce whatever the file contained into a usable host string.
 *
 * Exported for testing, and deliberately strict: this value decides where the
 * application sends its bearer token, so a value that cannot be understood is
 * refused rather than passed through and concatenated into a URL.
 *
 * `pageProtocol` is a parameter rather than a direct `location` read so the
 * mixed-content rule can be tested both ways.
 */
export function normalizeApiHost(
  raw: unknown,
  pageProtocol: string = typeof location === 'undefined' ? 'https:' : location.protocol,
): string {
  if (typeof raw !== 'string') return DEFAULTS.apiHost;

  const candidate = raw.trim();

  // Explicitly empty is meaningful and is the recommended production value:
  // "the API is on this origin".
  if (candidate === '') return '';

  // A protocol-relative URL ("//other-host") is cross-origin while looking
  // relative, and `isTrustedApiUrl` already refuses to treat it as same-origin.
  // Rejecting it here keeps the two consistent.
  if (candidate.startsWith('//')) {
    warn(`apiHost "${candidate}" is protocol-relative; ignoring it.`);
    return DEFAULTS.apiHost;
  }

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    warn(`apiHost "${candidate}" is not an absolute URL; ignoring it.`);
    return DEFAULTS.apiHost;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    warn(`apiHost "${candidate}" does not use http or https; ignoring it.`);
    return DEFAULTS.apiHost;
  }

  // A query or fragment cannot survive having a path concatenated onto it, so
  // it can only be a mistake.
  if (url.search !== '' || url.hash !== '') {
    warn(`apiHost "${candidate}" carries a query or fragment; ignoring it.`);
    return DEFAULTS.apiHost;
  }

  // An https page cannot call an http host: the browser blocks it as mixed
  // content, so honouring the value would produce an application that starts
  // and then fails every request. Same-origin is the better outcome, and the
  // warning names the real cause.
  if (url.protocol === 'http:' && pageProtocol === 'https:') {
    warn(
      `apiHost "${candidate}" is plain http but this page is https; ` +
        'the browser would block every request as mixed content. Falling back to same origin.',
    );
    return '';
  }

  // Rebuild from the parsed URL rather than returning the input: this
  // normalises away a trailing slash, which matters because `apiUrl` appends
  // "/" and "host//api/user" is not the same path as "host/api/user".
  const path = url.pathname.replace(/\/+$/, '');
  return url.origin + path;
}

/** Replace the active configuration. For tests and for `loadAppConfig`. */
export function setAppConfig(raw: unknown, pageProtocol?: string): AppConfig {
  const source = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  current = {
    apiHost: normalizeApiHost(source['apiHost'], pageProtocol),
    languages: normalizeLanguages(source['languages']),
  };
  return current;
}

/**
 * Fetch and apply the configuration. Never rejects -- a deployment that has not
 * been given the file still gets a working application, and the reason is
 * logged once rather than swallowed.
 */
export async function loadAppConfig(fetchImpl: typeof fetch = fetch): Promise<AppConfig> {
  try {
    // cache: 'no-store' matters. The file is deliberately small and mutable; a
    // cached copy would keep a corrected host wrong for as long as the browser
    // held it.
    const response = await fetchImpl(APP_CONFIG_URL, { cache: 'no-store' });
    if (!response.ok) {
      warn(`${APP_CONFIG_URL} returned ${response.status}; using defaults.`);
      current = DEFAULTS;
      return current;
    }
    return setAppConfig(await response.json());
  } catch (err) {
    warn(`could not load ${APP_CONFIG_URL}; using defaults.`);
    // eslint-disable-next-line no-console
    console.warn(err);
    current = DEFAULTS;
    return current;
  }
}

function warn(message: string): void {
  // eslint-disable-next-line no-console
  console.warn(`app-config: ${message}`);
}
